import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const toggle = (set, v) => { const s = new Set(set); s.has(v) ? s.delete(v) : s.add(v); return s; };

export const TeamPresetPublishDialog = ({ open, onClose, defaultName, existing, onPublish }) => {
  const [name, setName] = useState("");
  const [teams, setTeams] = useState(new Set());
  const [userIds, setUserIds] = useState(new Set());
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    const ex = (existing || []).find(p => p.name.toLowerCase() === (defaultName || "").toLowerCase());
    setName(defaultName || "");
    setTeams(new Set(ex?.target_teams || []));
    setUserIds(new Set(ex?.target_user_ids || []));
    setQ("");
    axios.get(`${API}/admin/all-users`, { headers: getAuthHeader() })
      .then(r => setUsers((r.data || []).filter(u => u.approved !== false)))
      .catch(() => setUsers([]));
  }, [open, defaultName, existing]);

  const allTeams = useMemo(() => [...new Set(users.map(u => u.team).filter(Boolean))].sort(), [users]);
  const shownUsers = users.filter(u => !q || `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase()));
  const everyone = teams.size === 0 && userIds.size === 0;
  const recipients = useMemo(() => users
    .filter(u => everyone || teams.has(u.team) || userIds.has(u.id))
    .sort((a, b) => (a.team || "").localeCompare(b.team || "") || (a.name || "").localeCompare(b.name || "")),
  [users, teams, userIds, everyone]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="team-preset-publish-dialog">
        <DialogHeader>
          <DialogTitle>Publish Team preset</DialogTitle>
          <DialogDescription>
            Pick teams and/or individual users. Leave both empty to share with everyone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input value={name} onChange={e => setName(e.target.value)} maxLength={40}
                 placeholder="Preset name" data-testid="team-preset-name" />
          <div>
            <div className="text-[11px] font-semibold text-slate-600 mb-1">Teams</div>
            <div className="flex flex-wrap gap-1.5" data-testid="team-preset-teams">
              {allTeams.map(t => (
                <button key={t} type="button" onClick={() => setTeams(toggle(teams, t))}
                        data-testid={`team-preset-team-${t.replace(/\s+/g, "-").toLowerCase()}`}
                        className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${teams.has(t)
                          ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-semibold text-slate-600 mb-1">
              Individual users {userIds.size > 0 && <span className="text-emerald-700">({userIds.size} selected)</span>}
            </div>
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search users…"
                   className="h-8 text-xs mb-1" data-testid="team-preset-user-search" />
            <div className="max-h-44 overflow-y-auto border rounded" data-testid="team-preset-users">
              {shownUsers.map(u => (
                <label key={u.id} className="flex items-center gap-2 px-2 py-1 text-[11px] hover:bg-slate-50 cursor-pointer"
                       data-testid={`team-preset-user-${u.id}`}>
                  <input type="checkbox" checked={userIds.has(u.id)} onChange={() => setUserIds(toggle(userIds, u.id))} />
                  <span className="font-medium">{u.name}</span>
                  <span className="text-slate-400 truncate">{u.email}</span>
                  <span className="ml-auto text-slate-500">{u.team}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="rounded border border-emerald-200 bg-emerald-50/50" data-testid="team-preset-recipients">
            <div className="px-2 py-1.5 text-[11px] font-semibold text-emerald-800 border-b border-emerald-200 flex justify-between">
              <span data-testid="team-preset-recipient-count">
                {recipients.length} {recipients.length === 1 ? "person" : "people"} will receive this
              </span>
              <span className="font-normal text-slate-500" data-testid="team-preset-audience">
                {everyone ? "Everyone" : `${teams.size} team(s) + ${userIds.size} user(s)`}
              </span>
            </div>
            <div className="max-h-32 overflow-y-auto">
              {recipients.length === 0 ? (
                <div className="px-2 py-2 text-[11px] text-slate-500">No one matches the current selection.</div>
              ) : recipients.map(u => (
                <div key={u.id} className="flex items-center gap-2 px-2 py-0.5 text-[11px]"
                     data-testid={`team-preset-recipient-${u.id}`}>
                  <span className="font-medium text-slate-800">{u.name}</span>
                  <span className="ml-auto text-slate-500">{u.team}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={!name.trim()} data-testid="team-preset-publish-confirm"
                  onClick={() => onPublish(name.trim(), [...teams], [...userIds])}>
            Publish
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
